/*
 * Copyright (c) 2023 by N-Ach (Pvt) Ltd
 *
 * ╔═╗ ╔╗    ╔═══╗  ╔╗
 * ║║╚╗║║    ║╔═╗║  ║║
 * ║╔╗╚╝║    ║║ ║╠══╣╚═╗
 * ║║╚╗║║╔══╗║╚═╝║╔═╣╔╗║
 * ║║ ║║║╚══╝║╔═╗║╚═╣║║║
 * ╚╝ ╚═╝    ╚╝ ╚╩══╩╝╚╝
 *
 * All rights reserved.
 *
 * This software is the confidential and proprietary information
 * of N-Ach (Pvt) Ltd. You shall not disclose such Confidential
 * Information and shall use it only in accordance with the terms
 * of the license agreement you entered into with N-Ach (Pvt) Ltd.
 *
 *
 * @Author Dilhari Jayathunga
 * @Date 12/12/2023
 */

import {
  Backdrop,
  Button,
  ButtonGroup,
  CircularProgress,
  IconButton,
  Paper,
  Typography,
  Dialog,
  DialogActions,
} from "@mui/material";
import React, { useState } from "react";
import styles from "./Profile.module.css";
import {
  useDeleteAddressMutation,
  useGetAllAddressesQuery,
} from "../Store/Services/AddressService";
import { Add, Delete, Edit } from "@mui/icons-material";
import AddAddress from "../Components/Address/AddAddress";
import { toast } from "react-toastify";

// ... (previous imports)

const AddressPage = () => {
  const { data, isSuccess, isLoading } = useGetAllAddressesQuery();
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [editAddressId, setEditAddressId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({
    open: false,
    userContactId: null,
  });

  const [deleteAddress] = useDeleteAddressMutation();

  const handleEditClick = (userContactId) => {
    setEditAddressId(userContactId);
    setOpenEdit(true);
  };

  const handleDeleteClick = (userContactId) => {
    setDeleteConfirm({
      open: true,
      userContactId: userContactId,
    });
  };

  const handleDeleteConfirm = async (userContactId) => {
    try {
      const deleted = await deleteAddress(deleteConfirm.userContactId).unwrap();
      toast.success("Address successfully deleted!", {
        position: "bottom-right",
      });
    } catch (err) {
      console.error("Failed to delete the address: ", err);
      toast.error("Erorr, something went wrong!", { position: "bottom-right" });
    }
    setDeleteConfirm({
      open: false,
      userContactId: null,
    });
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm({
      open: false,
      userContactId: null,
    });
  };

  return (
    <div className={styles.container}>
      <Paper className={styles.paper}>
        {isLoading && (
          <Backdrop
            sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={isLoading}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
        )}
        <div className={styles.addAddressContainer}>
          <Typography fontWeight={600} variant={"h6"}>
            Addresses
          </Typography>
          <Button onClick={() => setOpenAdd(true)}>
            <Add /> Add New Address
          </Button>
        </div>
        {isSuccess &&
          !isLoading &&
          data.map((address) => (
            <div
              key={address.userContactId}
              className={styles.addressContainer}
            >
              <div>
                <Typography variant="h6">{address.contactName}</Typography>
                <Typography>
                  {address.addLine3}, {address.addLine2}, {address.addLine1}
                </Typography>
                <Typography>
                  {address.areaName}, {address.stateName}
                </Typography>
              </div>
              <div>
                <ButtonGroup variant="text" aria-label="text button group">
                  <IconButton
                    aria-label="edit"
                    onClick={() => handleEditClick(address.userContactId)}
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    aria-label="delete"
                    onClick={() => handleDeleteClick(address.userContactId)}
                  >
                    <Delete />
                  </IconButton>
                </ButtonGroup>
              </div>
            </div>
          ))}
        <AddAddress
          open={openAdd}
          handleClose={() => setOpenAdd(false)}
          from="add"
        />
        <AddAddress
          open={openEdit}
          handleClose={() => setOpenEdit(false)}
          editAddressId={editAddressId}
          from="edit"
        />

        <Dialog
          open={deleteConfirm.open}
          onClose={handleDeleteCancel}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          sx={{ "& .MuiDialog-paper": { width: "40%" } }}
        >
          <Typography
            my={3}
            variant="h5"
            component="div"
            color="text.primary"
            textAlign="center"
            fontWeight="bold"
          >
            Delete Address?
          </Typography>
          <Typography
            my={3}
            component="div"
            color="text.primary"
            textAlign="center"
            fontWeight="400"
          >
            Are you sure you want to delete this Address?
          </Typography>
          <DialogActions>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: "1rem",
                marginRight: "4rem",
              }}
            >
              <Button
                onClick={handleDeleteCancel}
                style={{
                  borderRadius: "1rem",
                  margin: "0.5rem",
                  width: "10rem",
                }}
                fullWidth
                variant="contained"
              >
                No
              </Button>
              <Button
                onClick={handleDeleteConfirm}
                style={{
                  borderRadius: "1rem",
                  margin: "0.5rem",
                  width: "10rem",
                }}
                fullWidth
                variant="contained"
              >
                Yes
              </Button>
            </div>
          </DialogActions>
        </Dialog>
      </Paper>
    </div>
  );
};

export default AddressPage;
